import {Injectable} from '@angular/core';
import {BehaviorSubject} from "rxjs";
import {equalsIgnoreCase} from "@ui-kit/utils/static/string-util";
import {CurrentContextService} from "@ui-kit/security/service/current-context.service";
import {FlattenedUserGrant, UserGrantedLocation,} from "@ui-kit/security/model/user-features-response.interface";

export type ReducedAdminFlattenedUserGrant = Pick<FlattenedUserGrant,
  'featureCode' |
  'roleId' |
  'roleCode' |
  'rolePrincipleType' |
  'moduleCode' |
  'moduleOfferingTypeCode' |
  'offeringUuid' |
  'platformOfferingTypeCode' |
  'locationUuid' |
  'landlordUuid'
> & { 'moduleName': string };

export type GrantRestrictionLevel = 'GLOBAL' | 'LANDLORD' | 'LOCATION' | 'OFFERING';

export interface Offering {
  uuid: string;
  name: string;
}

export interface Location {
  uuid: string;
  name: string;
  offerings: Offering[];
}

export interface Landlord {
  uuid: string;
  name: string;
  locations: Location[];
}

export class SelectedAdminContext {
  private _selectedLandlord: Landlord | null = null;
  private _selectedLocation: Location | null = null;
  private _selectedOffering: Offering | null = null;

  constructor() {
    this._selectedLandlord = null;
    this._selectedLocation = null;
    this._selectedOffering = null;
  }

  get landlord(): Landlord | null {
    return this._selectedLandlord;
  }

  get location(): Location | null {
    return this._selectedLocation;
  }

  get offering(): Offering | null {
    return this._selectedOffering;
  }

  set landlord(val: Landlord | null) {
    this._selectedLandlord = val;
  }

  set location(val: Location | null) {
    this._selectedLocation = val;
  }

  set offering(val: Offering | null) {
    this._selectedOffering = val;
  }

  /**
   * Rebuild a real instance from a sessionStorage-parsed value. JSON.parse yields
   * a plain object whose class getters (landlord/location/offering) are gone, so
   * reading them off it returns undefined — rehydrate into a proper instance.
   * Handles both the serialized private-field shape and a plain public shape.
   */
  static fromJSON(data: any): SelectedAdminContext {
    const ctx = new SelectedAdminContext();
    if (data) {
      ctx.landlord = data._selectedLandlord ?? data.landlord ?? null;
      ctx.location = data._selectedLocation ?? data.location ?? null;
      ctx.offering = data._selectedOffering ?? data.offering ?? null;
    }
    return ctx;
  }

}

@Injectable({
  providedIn: 'root'
})
export class AdminContextService extends CurrentContextService<UserGrantedLocation> {
  private readonly selectedAdminContextKey = 'selectedAdminContext';

  private _selectedAdminContext: SelectedAdminContext = new SelectedAdminContext();
  public selectedAdminContextValueChanged$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly GLOBAL_ADMIN_FEATURES: string[] = ["rbac_", "internal_admin", "admin_"];
  private readonly GLOBAL_ADMIN_ROLES: string[] = ["internal_admin", "internal_access_admin", "internal_client_onboarding"];

  // Flattening the grant tree is O(grants) and the admin access map is read from
  // template getters on every change-detection tick (see menu-module/menu-section).
  // Both derive only from userGrants, so memoise them and invalidate on reset()
  // (which every userGrants change funnels through).
  private _flattenedAccessCache: FlattenedUserGrant[] | null = null;
  private _adminAccessCache: ReducedAdminFlattenedUserGrant[] | null = null;

  constructor() {
    super();
  }

  /**
   * Drops the derived caches, and the selection only when the whole session goes with
   * it (logout / expired token — {@code super.reset(true)} clears sessionStorage anyway).
   *
   * <p>It used to clear the selection unconditionally. Every {@code userGrants} write
   * funnels through here, so loading grants wrote an empty context straight over the
   * stored one and the selected landlord/location/offering was lost on every page
   * refresh — TF-6. Callers that mean "forget what is selected" now say so by calling
   * {@link resetSelectedContext} directly.</p>
   */
  override reset(resetSession = false) {
    this.invalidateAccessCache();
    if (resetSession) {
      this.resetSelectedContext();
    }
    super.reset(resetSession);
  }

  private invalidateAccessCache(): void {
    this._flattenedAccessCache = null;
    this._adminAccessCache = null;
  }

  override set userGrants(grants: UserGrantedLocation[]) {
    this.reset();
    super.userGrants = grants;
  }

  override get userGrants() {
    return super.userGrants;
  }

  resetSelectedContext() {
    this.selectedAdminContext = new SelectedAdminContext();
    this.selectedAdminContextValueChanged$.next(true);
  }

  public get selectedAdminContext(): SelectedAdminContext {
    const {landlord, location, offering} = this._selectedAdminContext;
    if ((!this._selectedAdminContext
        || !landlord || !location || !offering)
      && sessionStorage.getItem(this.selectedAdminContextKey)) {
      this._selectedAdminContext = SelectedAdminContext.fromJSON(
        JSON.parse(sessionStorage.getItem(this.selectedAdminContextKey)!));
    }
    return this._selectedAdminContext;
  }

  protected set selectedAdminContext(val: SelectedAdminContext) {
    this._selectedAdminContext = val;
    sessionStorage.setItem(this.selectedAdminContextKey, JSON.stringify(val));
  }

  get selectedLandlordUuid(): string | null {
    return this.selectedAdminContext.landlord?.uuid ?? null;
  }

  get selectedLocationUuid(): string | null {
    return this.selectedAdminContext.location?.uuid ?? null;
  }

  get selectedOfferingUuid(): string | null {
    return this.selectedAdminContext.offering?.uuid ?? null;
  }

  update(val: { landlord?: Landlord | null, location?: Location | null, offering?: Offering | null }) {
    const {landlord, location, offering} = val;

    const updatedAdminContext = this.selectedAdminContext;
    let emit = false;

    if (landlord !== undefined && landlord !== updatedAdminContext.landlord) {
      updatedAdminContext.landlord = landlord;
      emit = true;
    }

    if (location !== undefined && location !== updatedAdminContext.location) {
      updatedAdminContext.location = location;
      emit = true;
    }

    if (offering !== undefined && offering !== updatedAdminContext.offering) {
      updatedAdminContext.offering = offering;
      emit = true;
    }

    if (emit) {
      this.selectedAdminContext = updatedAdminContext;
      this.selectedAdminContextValueChanged$.next(true);
    }
  }

  /**
   * Retrieves the access map for admin users with specific filtering and field reduction.
   * This method overrides the base getAccessMap method to add admin-specific logic.
   *
   * @override
   * @param {function} [additionalFilter] - Optional additional filter function to be combined with the admin filter.
   * @returns {ReducedAdminFlattenedUserGrant[]} An array of reduced and filtered admin user grants.
   */
  //TODO cant remember why extra admin filter was added???, may not be needed anymore since admin is own app now
  public override accessMap<ReducedAdminFlattenedUserGrant>(additionalFilter?: (access: ReducedAdminFlattenedUserGrant) => boolean): ReducedAdminFlattenedUserGrant[] {
    const accessMap = (this._adminAccessCache ??= this.buildAdminAccessMap()) as ReducedAdminFlattenedUserGrant[];
    return additionalFilter ? accessMap.filter(additionalFilter) : accessMap;
  }

  private buildAdminAccessMap(): ReducedAdminFlattenedUserGrant[] {
    const adminFilter = (access: FlattenedUserGrant) =>
      this.GLOBAL_ADMIN_FEATURES.some(code => access.featureCode.includes(code)) ||
      this.GLOBAL_ADMIN_ROLES.some(code => access.roleCode.includes(code));

    return this.filteredAccessMap(adminFilter).map(access => ({
      featureCode: access.featureCode,
      roleId: access.roleId,
      roleCode: access.roleCode,
      rolePrincipleType: access.rolePrincipleType,
      moduleCode: access.moduleCode,
      moduleName: access.name,
      moduleOfferingTypeCode: access.moduleOfferingTypeCode,
      offeringUuid: access.offeringUuid,
      platformOfferingTypeCode: access.platformOfferingTypeCode,
      locationUuid: access.locationUuid,
      landlordUuid: access.landlordUuid,
    }));
  }

  /**
   * Internal method to filter and flatten the user's access grants.
   * This method implements the core logic for retrieving and filtering access grants.
   *
   * @protected
   * @param {function} [filterPredicate] - Optional function to filter the access grants.
   * @returns {FlattenedUserGrant[]} An array of flattened and filtered user grants.
   */
  protected override filteredAccessMap<FlattenedUserGrant>(filterPredicate?: (access: FlattenedUserGrant) => boolean): FlattenedUserGrant[] {
    const allGrants = (this._flattenedAccessCache ??= this.buildFlattenedAccessMap()) as FlattenedUserGrant[];
    return filterPredicate ? allGrants.filter(filterPredicate) : allGrants;
  }

  private buildFlattenedAccessMap(): FlattenedUserGrant[] {
    return this.userGrants?.flatMap(location =>
      location.userGrantedOfferings.flatMap(offering =>
        offering.userGrantedModules.flatMap(module =>
          module.userGrantedRoles.flatMap(role =>
            role.userGrantedFeatures.map(feature => ({
              ...location,
              ...offering,
              ...module,
              ...role,
              ...feature
            } as unknown as FlattenedUserGrant))
          )
        )
      )) ?? [];
  }

  public checkAccessByRestrictionLevel(featureCode: string, restrictionLevel: GrantRestrictionLevel): boolean {
    const flatGrants = this.accessMap((grant: any) => grant.featureCode === featureCode);

    const filteredGrants = flatGrants.filter((grant: any) => {
      switch (restrictionLevel) {
        case 'GLOBAL':
          return true;
        case 'LANDLORD':
          return this.selectedLandlordUuid != null
            && this.grantMatches(grant.landlordUuid, this.selectedLandlordUuid);
        case 'LOCATION':
          return this.selectedLocationUuid != null
            && this.grantMatches(grant.landlordUuid, this.selectedLandlordUuid)
            && this.grantMatches(grant.locationUuid, this.selectedLocationUuid);
        case 'OFFERING':
          return this.selectedOfferingUuid != null
            && this.grantMatches(grant.landlordUuid, this.selectedLandlordUuid)
            && this.grantMatches(grant.locationUuid, this.selectedLocationUuid)
            && this.grantMatches(grant.offeringUuid, this.selectedOfferingUuid);
        default:
          return false;
      }
    });
    return filteredGrants.length > 0;
  }

  /**
   * Whether a grant's entity uuid authorises the selected entity. A global
   * (super-user / O-8) grant carries a null entity uuid and matches ANY selected
   * entity — including landlords/locations/offerings created after login — so
   * internal super users see the contextual menus without a token refresh.
   * Entity-scoped (console) grants keep exact-match semantics.
   */
  private grantMatches(grantUuid: string | null, selectedUuid: string | null): boolean {
    return grantUuid == null || equalsIgnoreCase(grantUuid, selectedUuid);
  }

}
